import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    getMilestones, getDependencies,
    type Task, type Milestone, type TaskDependency,
} from '../../packages/api-client/pm-api';
import {
    getPhases, schedulePlan, generatePlan,
    listBaselines, createBaseline, deleteBaseline, getVariance,
    type Phase, type Schedule, type ScheduledTask, type Baseline,
} from '../../packages/api-client/plan-api';
import { Dialog, StateView } from '../../packages/ui';
import { GanttWithTable } from '../../components/GanttTable';
import { PlanTable } from './PlanTable';
import { PlanMilestones } from './PlanMilestones';
import { toDateInputValue } from '../../utils/date';
import { planErrorMessage, getAllTasks, mapLimit } from './planData';
import './PlanView.css';

type MilestoneHealth = { health: string; projected?: string | null; target?: string | null; floatDays?: number | null };
type PhaseRollup = { start?: string | null; end?: string | null; taskCount: number; doneCount: number };

/** 4xx means the server refused before writing (validation/permission run first). */
function refusedBeforeWrite(err: unknown): boolean {
    const status = (err as { response?: { status?: number } })?.response?.status;
    return status !== undefined && status >= 400 && status < 500;
}

type Change = { task: Task; from: string; to: string };

/** Tasks whose dates the schedule would change, in schedule order. */
function scheduleChanges(s: Schedule, tasks: Task[]): { changes: Change[]; unchanged: number } {
    const byId = new Map(tasks.map(t => [t.id, t]));
    const changes: Change[] = [];
    let unchanged = 0;
    for (const st of s.tasks) {
        const t = byId.get(st.task_id);
        if (!t) continue;
        const from = `${toDateInputValue(t.start_date) || '—'} → ${toDateInputValue(t.due_date) || '—'}`;
        const to = `${toDateInputValue(st.start)} → ${toDateInputValue(st.due)}`;
        if (from === to) unchanged++;
        else changes.push({ task: t, from, to });
    }
    return { changes, unchanged };
}

type ScheduleDialogState =
    | { phase: 'closed' }
    | { phase: 'previewing' }
    | { phase: 'preview-error'; message: string }
    | { phase: 'ready' | 'applying'; preview: Schedule }
    | { phase: 'apply-error'; preview: Schedule; message: string; mayHaveWritten: boolean; reloadError?: string };

/**
 * PlanView — the Planning › Plan view: toolbar (auto-schedule preview/apply,
 * AI plan generation, baselines), schedule summary with the critical path,
 * the plan table editor, the Gantt (with a table alternative) and milestones.
 * Rendered inside the project shell; the old /projects/:id/plan route
 * redirects here.
 */
export function PlanView({ projectId: id }: { projectId: string }) {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [milestones, setMilestones] = useState<Milestone[]>([]);
    const [phases, setPhases] = useState<Phase[]>([]);
    const [dependsOn, setDependsOn] = useState<Record<string, TaskDependency[]>>({});
    const [depFailures, setDepFailures] = useState(0);
    const [truncated, setTruncated] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<Baseline | null>(null);
    // Current dependency map and failed task ids, read by reload() so an edit
    // refetches only the tasks it touched.
    const depsRef = useRef<Record<string, TaskDependency[]>>({});
    const depFailedRef = useRef<Set<string>>(new Set());
    const [schedule, setSchedule] = useState<Schedule | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [baselines, setBaselines] = useState<Baseline[]>([]);
    const [baselinesError, setBaselinesError] = useState<string | null>(null);
    const [selectedBaselineId, setSelectedBaselineId] = useState<string>('');
    const [varianceError, setVarianceError] = useState<string | null>(null);
    const [baselineByTask, setBaselineByTask] = useState<Map<string, { start?: string; due?: string }> | undefined>(undefined);
    const [slipByTask, setSlipByTask] = useState<Map<string, number> | undefined>(undefined);
    const [baselineMilestones, setBaselineMilestones] = useState<Map<string, string> | undefined>(undefined);
    const [dialog, setDialog] = useState<ScheduleDialogState>({ phase: 'closed' });
    // After an apply: what actually landed, read back from the tasks.
    const [applyNotice, setApplyNotice] = useState<string | null>(null);
    const [genOpen, setGenOpen] = useState(false);
    const debounceRef = useRef<number | undefined>(undefined);
    useEffect(() => () => {
        if (debounceRef.current !== undefined) window.clearTimeout(debounceRef.current);
    }, [id]);

    const criticalIds = new Set(schedule?.critical_path ?? []);
    const scheduleByTask: Record<string, ScheduledTask> = Object.fromEntries(
        (schedule?.tasks ?? []).map(s => [s.task_id, s])
    );
    const milestoneHealth = schedule?.milestones?.length
        ? new Map<string, MilestoneHealth>(schedule.milestones.map(mp => [mp.milestone_id, {
            health: mp.health, projected: mp.projected, target: mp.target, floatDays: mp.float_days,
        }]))
        : undefined;
    const phaseRollups = schedule?.phases?.length
        ? new Map<string, PhaseRollup>(schedule.phases.map(pr => [pr.phase_id, {
            start: pr.start, end: pr.end, taskCount: pr.task_count, doneCount: pr.done_count,
        }]))
        : undefined;

    const loadBaselines = useCallback(async () => {
        try {
            const bl = await listBaselines(id);
            setBaselines(bl);
            setBaselinesError(null);
            // Auto-select the newest baseline (list is newest-first) until the user picks.
            setSelectedBaselineId(prev => (prev === '' && bl.length > 0 ? bl[0].id : prev));
        } catch (err) {
            setBaselinesError(planErrorMessage(err, 'load baselines'));
        }
    }, [id]);

    /** Read-only projection (apply:false) — nothing is written. */
    const previewSchedule = useCallback(async () => {
        try {
            const s = await schedulePlan(id, false);
            setSchedule(s);
            setPreviewError(null);
        } catch (err) {
            setSchedule(null);
            setPreviewError(planErrorMessage(err, 'compute the schedule'));
        }
    }, [id]);

    /**
     * Loads tasks, milestones and phases, and dependencies for `depsFor`
     * ('all', or just these task ids) plus any task not loaded before; other
     * tasks keep their loaded dependencies. There is no project-level
     * dependencies endpoint, so it is one request per task, at most 6 at a
     * time. Returns the tasks. Throws on failure.
     */
    const reload = useCallback(async (depsFor: 'all' | string[] = []): Promise<Task[]> => {
        const [{ tasks: taskList, truncated: cut }, m, ph] = await Promise.all([
            getAllTasks(id),
            getMilestones(id),
            getPhases(id),
        ]);
        const known = depsRef.current;
        const fetchIds = depsFor === 'all'
            ? taskList.map(t => t.id)
            : taskList.map(t => t.id).filter(tid => depsFor.includes(tid) || !(tid in known));
        const fetched = await mapLimit(fetchIds, 6, tid =>
            getDependencies(tid).then(d => ({ tid, d, ok: true }), () => ({ tid, d: [] as TaskDependency[], ok: false })));
        const failed = new Set([...depFailedRef.current].filter(tid => taskList.some(t => t.id === tid)));
        const next: Record<string, TaskDependency[]> = {};
        for (const t of taskList) next[t.id] = known[t.id] ?? [];
        for (const f of fetched) {
            next[f.tid] = f.d;
            if (f.ok) failed.delete(f.tid); else failed.add(f.tid);
        }
        depsRef.current = next;
        depFailedRef.current = failed;
        setTasks(taskList);
        setTruncated(cut);
        setMilestones(m);
        setPhases(ph);
        setDependsOn(next);
        setDepFailures(failed.size);
        return taskList;
    }, [id]);

    const initialLoad = useCallback(async () => {
        setLoading(true);
        try {
            await reload('all');
            setLoadError(null);
            setLoading(false);
            previewSchedule();
        } catch (err) {
            setLoadError(planErrorMessage(err, 'load the plan'));
            setLoading(false);
        }
    }, [reload, previewSchedule]);

    useEffect(() => { initialLoad(); }, [initialLoad]);
    useEffect(() => { loadBaselines(); }, [loadBaselines]);

    // Variance for the selected baseline.
    useEffect(() => {
        if (!selectedBaselineId) {
            setBaselineByTask(undefined);
            setSlipByTask(undefined);
            setBaselineMilestones(undefined);
            setVarianceError(null);
            return;
        }
        let stale = false;
        getVariance(id, selectedBaselineId).then(variance => {
            if (stale) return;
            const btMap = new Map<string, { start?: string; due?: string }>();
            const slipMap = new Map<string, number>();
            for (const tv of variance.tasks) {
                btMap.set(tv.task_id, { start: tv.baseline_start ?? undefined, due: tv.baseline_due ?? undefined });
                if (tv.slip_days != null) slipMap.set(tv.task_id, tv.slip_days);
            }
            const bmMap = new Map<string, string>();
            for (const mv of variance.milestones) {
                if (mv.baseline_target != null) bmMap.set(mv.milestone_id, mv.baseline_target);
            }
            setBaselineByTask(btMap);
            setSlipByTask(slipMap);
            setBaselineMilestones(bmMap);
            setVarianceError(null);
        }).catch(err => {
            if (stale) return;
            setBaselineByTask(undefined);
            setSlipByTask(undefined);
            setBaselineMilestones(undefined);
            setVarianceError(planErrorMessage(err, 'load the variance for this baseline'));
        });
        return () => { stale = true; };
    }, [id, selectedBaselineId]);

    const handleChanged = useCallback((depsChangedFor?: string[]) => {
        reload(depsChangedFor ?? []).catch(err => toast.error(planErrorMessage(err, 'reload the plan')));
        if (debounceRef.current !== undefined) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(previewSchedule, 400);
    }, [reload, previewSchedule]);

    // ── Auto-schedule: preview first, apply only on an explicit click ──────
    const openSchedulePreview = useCallback(() => {
        setDialog({ phase: 'previewing' });
        schedulePlan(id, false)
            .then(preview => setDialog({ phase: 'ready', preview }))
            .catch(err => setDialog({ phase: 'preview-error', message: planErrorMessage(err, 'compute the schedule') }));
    }, [id]);

    const applySchedule = useCallback(async (preview: Schedule) => {
        setDialog({ phase: 'applying', preview });
        let applied: Schedule;
        try {
            applied = await schedulePlan(id, true);
        } catch (err) {
            const mayHaveWritten = !refusedBeforeWrite(err);
            setDialog({ phase: 'apply-error', preview, message: planErrorMessage(err, 'apply the schedule'), mayHaveWritten });
            // A failure after writing began can leave some dates changed: show
            // the real state, and recompute the summary so it isn't stale.
            if (mayHaveWritten) {
                reload().catch(e => setDialog(d => d.phase === 'apply-error'
                    ? { ...d, reloadError: planErrorMessage(e, 'reload the plan') } : d))
                    .finally(() => { previewSchedule(); });
            }
            return;
        }
        setSchedule(applied);
        setPreviewError(null);
        setDialog({ phase: 'closed' });
        // The server applies task by task; read the dates back rather than trust the 200.
        let fresh: Task[];
        try {
            fresh = await reload();
        } catch (err) {
            setApplyNotice(`The schedule was applied, but the plan couldn't be reloaded to confirm the new dates. ${planErrorMessage(err, 'reload the plan')}`);
            return;
        }
        // Only tasks in the reloaded list can be checked (and named); the
        // schedule may cover tasks this viewer can't see.
        const byId = new Map(fresh.map(t => [t.id, t]));
        const visible = applied.tasks.filter(st => byId.has(st.task_id));
        const missed = visible.filter(st => {
            const t = byId.get(st.task_id)!;
            return toDateInputValue(t.start_date) !== toDateInputValue(st.start) || toDateInputValue(t.due_date) !== toDateInputValue(st.due);
        });
        if (missed.length > 0) {
            const names = missed.map(st => byId.get(st.task_id)!.title).join(', ');
            setApplyNotice(`The schedule was applied, but not everywhere. Of the ${visible.length} scheduled tasks you can see, ${missed.length} kept their previous dates: ${names}.`);
        } else {
            setApplyNotice(null);
            toast.success('Schedule applied');
        }
    }, [id, reload, previewSchedule]);

    // ── Baselines ─────────────────────────────────────────────────────────
    const handleSetBaseline = useCallback(() => {
        createBaseline(id)
            .then(() => { toast.success('Baseline saved'); return loadBaselines(); })
            .catch(err => toast.error(planErrorMessage(err, 'save a baseline')));
    }, [id, loadBaselines]);

    const handleDeleteBaseline = useCallback((b: Baseline) => {
        deleteBaseline(id, b.id)
            .then(() => { setConfirmDelete(null); setSelectedBaselineId(''); toast.success('Baseline deleted'); return loadBaselines(); })
            .catch(err => { setConfirmDelete(null); toast.error(planErrorMessage(err, 'delete the baseline')); });
    }, [id, loadBaselines]);

    if (loading) return <StateView state="loading" label="Loading plan" lines={6} />;
    if (loadError) return <StateView state="error" title="The plan couldn't be loaded" message={loadError} onRetry={initialLoad} />;

    const criticalTasks = (schedule?.critical_path ?? [])
        .map(tid => ({ task: tasks.find(t => t.id === tid), st: scheduleByTask[tid] }))
        .filter(x => x.task);

    return (
        <div className="plan-view">
            <div className="plan-toolbar">
                <button type="button" className="btn btn-primary btn-sm" onClick={openSchedulePreview}>Auto-schedule…</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setGenOpen(true)}>Generate plan with AI…</button>
                <span className="plan-toolbar-sep" aria-hidden="true" />
                <button type="button" className="btn btn-ghost btn-sm" onClick={handleSetBaseline}>Set baseline</button>
                {baselines.length > 0 && (
                    <label className="plan-compare">
                        <span>Compare against</span>
                        <select className="form-input" value={selectedBaselineId} onChange={e => setSelectedBaselineId(e.target.value)}>
                            <option value="">None</option>
                            {baselines.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
                        </select>
                    </label>
                )}
                {selectedBaselineId && (
                    <button type="button" className="btn btn-ghost btn-sm plan-danger"
                        onClick={() => setConfirmDelete(baselines.find(x => x.id === selectedBaselineId) ?? null)}>Delete baseline</button>
                )}
            </div>

            {baselinesError && (
                <StateView state="partial" message={baselinesError} onRetry={loadBaselines}><></></StateView>
            )}
            {varianceError && <p role="alert" className="plan-notice plan-notice--error">{varianceError}</p>}
            {truncated && (
                <p role="status" className="plan-notice plan-notice--warn">
                    Showing the first 10,000 tasks. The plan table, schedule check and Gantt don't include the rest.
                </p>
            )}
            {depFailures > 0 && (
                <p role="alert" className="plan-notice plan-notice--warn">
                    Dependencies for {depFailures} task{depFailures !== 1 ? 's' : ''} couldn't be loaded, so the plan table shows them without dependencies.
                </p>
            )}
            {applyNotice && (
                <div role="alert" className="plan-notice plan-notice--warn">
                    <span>{applyNotice}</span>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setApplyNotice(null)}>Dismiss</button>
                </div>
            )}

            <section className="plan-summary card" aria-label="Schedule summary">
                {previewError ? (
                    <p role="alert" className="plan-notice--error">{previewError}</p>
                ) : schedule ? (
                    <>
                        <p className="plan-summary-headline">
                            Projected completion <strong>{toDateInputValue(schedule.end_date) || '—'}</strong>
                            <span className="plan-summary-meta"> · preview, not yet applied to task dates unless you apply it</span>
                        </p>
                        <h3 className="plan-subheading">Critical path ({criticalTasks.length} task{criticalTasks.length !== 1 ? 's' : ''}, zero slack)</h3>
                        {criticalTasks.length === 0 ? (
                            <p className="plan-muted">No tasks are on the critical path.</p>
                        ) : (
                            <ol className="plan-critical-list" aria-label="Critical path tasks">
                                {criticalTasks.map(({ task, st }) => (
                                    <li key={task!.id}>
                                        <span>{task!.title}</span>
                                        {st && <span className="plan-muted"> {toDateInputValue(st.start)} → {toDateInputValue(st.due)}</span>}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </>
                ) : (
                    <p className="plan-muted">Computing the schedule…</p>
                )}
            </section>

            <div className="plan-panes">
                <section className="plan-pane plan-pane--table" data-testid="plan-table-pane" aria-label="Plan table">
                    <PlanTable
                        projectId={id}
                        phases={phases}
                        tasks={tasks}
                        milestones={milestones}
                        dependsOn={dependsOn}
                        scheduleByTask={scheduleByTask}
                        phaseRollups={phaseRollups}
                        onChanged={handleChanged}
                    />
                </section>
                <section className="plan-pane plan-pane--gantt" aria-label="Gantt">
                    <GanttWithTable
                        tasks={tasks}
                        milestones={milestones}
                        phases={phases}
                        criticalTaskIds={criticalIds}
                        baselineByTask={baselineByTask}
                        slipByTask={slipByTask}
                        baselineMilestones={baselineMilestones}
                        milestoneHealth={milestoneHealth}
                        phaseRollups={phaseRollups}
                    />
                </section>
            </div>

            <PlanMilestones projectId={id} milestones={milestones} milestoneHealth={milestoneHealth} onChanged={handleChanged} />

            <ScheduleDialog state={dialog} tasks={tasks} onClose={() => setDialog({ phase: 'closed' })}
                onRetryPreview={openSchedulePreview} onApply={applySchedule} />
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete baseline?"
                footer={<>
                    <button type="button" className="btn btn-ghost btn-md" onClick={() => setConfirmDelete(null)}>Cancel</button>
                    <button type="button" className="btn btn-danger btn-md" onClick={() => confirmDelete && handleDeleteBaseline(confirmDelete)}>Delete</button>
                </>}>
                <p>Delete the baseline &ldquo;{confirmDelete?.label}&rdquo;? Variance against it will no longer be available. This can&apos;t be undone.</p>
            </Dialog>
            <GeneratePlanDialog open={genOpen} projectId={id} onClose={() => setGenOpen(false)}
                onGenerated={() => { setGenOpen(false); handleChanged(); }} />
        </div>
    );
}

function ScheduleDialog({ state, tasks, onClose, onRetryPreview, onApply }: {
    state: ScheduleDialogState; tasks: Task[]; onClose: () => void; onRetryPreview: () => void; onApply: (s: Schedule) => void;
}) {
    if (state.phase === 'closed') return null;
    const preview = 'preview' in state ? state.preview : null;
    const diff = preview ? scheduleChanges(preview, tasks) : null;
    const busy = state.phase === 'applying';
    return (
        <Dialog open onClose={busy ? () => {} : onClose} title="Auto-schedule preview" closeOnBackdrop={false}
            footer={<>
                <button type="button" className="btn btn-ghost btn-md" onClick={onClose} disabled={busy}>Cancel</button>
                {preview && (
                    <button type="button" className="btn btn-primary btn-md" disabled={busy || !diff || diff.changes.length === 0}
                        aria-busy={busy || undefined} onClick={() => onApply(preview)}>
                        {busy ? 'Applying…' : 'Apply schedule'}
                    </button>
                )}
            </>}>
            {state.phase === 'previewing' && <StateView state="loading" label="Computing schedule preview" />}
            {state.phase === 'preview-error' && (
                <StateView state="error" title="The schedule couldn't be computed" message={state.message} onRetry={onRetryPreview} />
            )}
            {state.phase === 'apply-error' && (
                <p role="alert" className="plan-notice plan-notice--error">
                    {state.message}{' '}
                    {state.mayHaveWritten
                        ? 'Some task dates may have changed before the failure; the plan is reloaded to show the current dates.'
                        : 'No task dates were changed.'}
                    {state.reloadError && ` ${state.reloadError}`}
                </p>
            )}
            {preview && diff && (
                <>
                    <p>
                        Projected completion <strong>{toDateInputValue(preview.end_date) || '—'}</strong>.{' '}
                        {diff.changes.length === 0
                            ? 'Every task already has these dates — nothing to apply.'
                            : `Applying sets new start and due dates on ${diff.changes.length} task${diff.changes.length !== 1 ? 's' : ''}${diff.unchanged ? ` (${diff.unchanged} unchanged)` : ''}.`}
                    </p>
                    {diff.changes.length > 0 && (
                        <div className="table-wrapper ui-table-scroll" role="region" tabIndex={0} aria-label="Date changes">
                            <table className="table ui-table ui-table--compact" data-testid="schedule-changes">
                                <thead><tr><th scope="col">Task</th><th scope="col">Current</th><th scope="col">Proposed</th><th scope="col">Critical</th></tr></thead>
                                <tbody>
                                    {diff.changes.map(c => (
                                        <tr key={c.task.id}>
                                            <th scope="row">{c.task.title}</th>
                                            <td>{c.from}</td>
                                            <td>{c.to}</td>
                                            <td>{preview.critical_path.includes(c.task.id) ? 'Yes' : 'No'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </Dialog>
    );
}

function GeneratePlanDialog({ open, projectId, onClose, onGenerated }: {
    open: boolean; projectId: string; onClose: () => void; onGenerated: () => void;
}) {
    const [prompt, setPrompt] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => { if (!open) { setError(null); setBusy(false); } }, [open]);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        if (!prompt.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await generatePlan(projectId, prompt.trim());
            toast.success('Plan generated');
            setPrompt('');
            onGenerated();
        } catch (err) {
            setError(planErrorMessage(err, 'generate a plan'));
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onClose={busy ? () => {} : onClose} title="Generate plan with AI" closeOnBackdrop={false}
            footer={<>
                <button type="button" className="btn btn-ghost btn-md" onClick={onClose} disabled={busy}>Cancel</button>
                <button type="submit" form="plan-generate-form" className="btn btn-primary btn-md" disabled={busy || !prompt.trim()} aria-busy={busy || undefined}>
                    {busy ? 'Generating…' : 'Generate'}
                </button>
            </>}>
            <form id="plan-generate-form" onSubmit={submit}>
                <label className="form-label" htmlFor="plan-generate-prompt">Describe the project plan</label>
                <textarea id="plan-generate-prompt" className="form-input" rows={5} value={prompt} autoFocus
                    onChange={e => setPrompt(e.target.value)} disabled={busy} />
                <p className="plan-muted">The AI adds new tasks and phases to this project. Existing tasks are not changed.</p>
                {error && <p role="alert" className="plan-notice plan-notice--error">{error}</p>}
            </form>
        </Dialog>
    );
}

/**
 * PlanPage — the old standalone /projects/:id/plan route. It used to render its
 * own project header, which dropped the full project-detail chrome
 * (Overview/Tasks/Board/…) and forced a Back click to return. The plan now
 * lives as an in-page tab inside ProjectDetailPage, so this route just redirects
 * there, forwarding any existing query params.
 */
export function PlanPage() {
    const { id: projectId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (!projectId) return;
        const next = new URLSearchParams(searchParams);
        next.set('tab', 'Plan');
        navigate(`/projects/${projectId}?${next.toString()}`, { replace: true });
    }, [projectId, searchParams, navigate]);

    return null;
}
